import { createLocalStorageRepository } from './localStorageRepository'
import type { CrudRepository } from './repository'

type Entity = { id: string; order: number }

export function createFirestoreRepository<T extends Entity>(
  collectionName: string,
  seed: () => T[],
): CrudRepository<T> {
  const fallbackRepository = createLocalStorageRepository<T>(`bb_firestore_fallback_${collectionName}`, seed)

  async function getCollectionDeps() {
    const firestoreModule = await import('firebase/firestore')
    const firebaseModule = await import('./firebase')
    const collectionRef = firestoreModule.collection(firebaseModule.firestore, collectionName)

    return {
      ...firestoreModule,
      collectionRef,
    }
  }

  function ensureSeeded(items: T[]) {
    return items.length > 0 ? items : seed()
  }

  function mergeWithFallback(remoteItems: T[], fallbackItems: T[]) {
    const baseItems = ensureSeeded(fallbackItems)
    if (remoteItems.length === 0) return baseItems

    const remoteIds = new Set(remoteItems.map((item) => item.id))
    const fallbackById = new Map(baseItems.map((item) => [item.id, item]))
    const mergedRemoteItems = remoteItems.map((remoteItem) => {
      const fallbackItem = fallbackById.get(remoteItem.id)
      if (!fallbackItem) return remoteItem

      const mergedItem = { ...fallbackItem, ...remoteItem } as T
      const remoteCover = (remoteItem as T & { coverImage?: string | null }).coverImage
      const fallbackCover = (fallbackItem as T & { coverImage?: string | null }).coverImage
      if (
        fallbackCover &&
        fallbackCover !== '/modi-cafe-logo.png' &&
        (!remoteCover || remoteCover === '/modi-cafe-logo.png')
      ) {
        ;(mergedItem as T & { coverImage?: string }).coverImage = fallbackCover
      }

      return mergedItem
    })

    return [
      ...baseItems.filter((item) => !remoteIds.has(item.id)),
      ...mergedRemoteItems,
    ].sort((first, second) => first.order - second.order)
  }

  return {
    async list() {
      try {
        const { collectionRef, getDocs, orderBy, query } = await getCollectionDeps()
        const snapshot = await getDocs(query(collectionRef, orderBy('order', 'asc')))
        const items = snapshot.docs.map((item) => item.data() as T)
        return mergeWithFallback(items, await fallbackRepository.list())
      } catch {
        return fallbackRepository.list()
      }
    },

    async create(input) {
      const items = await this.list()
      const nextOrder =
        input.order ?? (items.length > 0 ? Math.max(...items.map((item) => item.order)) + 10 : 10)
      const nextId = input.id ?? crypto.randomUUID()
      const created = { ...input, id: nextId, order: nextOrder } as T

      try {
        const firebaseModule = await import('./firebase')
        await firebaseModule.ensureFirebaseAuth()
        const { collectionRef, doc, setDoc } = await getCollectionDeps()
        await setDoc(doc(collectionRef, nextId), created)
      } catch {
        await fallbackRepository.create(created)
      }
      return created
    },

    async update(id, patch) {
      const current = (await this.list()).find((item) => item.id === id)
      if (!current) throw new Error(`No record with id "${id}" in "${collectionName}"`)
      const nextItem = { ...current, ...patch } as T

      try {
        const firebaseModule = await import('./firebase')
        await firebaseModule.ensureFirebaseAuth()
        const { collectionRef, doc, setDoc } = await getCollectionDeps()
        const nextRef = doc(collectionRef, id)
        await setDoc(nextRef, nextItem, { merge: true })
      } catch (error) {
        await fallbackRepository.update(id, patch)
        throw error
      }
      return nextItem
    },

    async remove(id) {
      try {
        const firebaseModule = await import('./firebase')
        await firebaseModule.ensureFirebaseAuth()
        const { collectionRef, deleteDoc, doc } = await getCollectionDeps()
        await deleteDoc(doc(collectionRef, id))
      } catch {
        await fallbackRepository.remove(id)
      }
    },

    async reorder(orderedIds) {
      try {
        const firebaseModule = await import('./firebase')
        await firebaseModule.ensureFirebaseAuth()
        const current = await this.list()
        const orderedIdSet = new Set(orderedIds)
        const fullOrderedIds = [
          ...orderedIds,
          ...current.filter((item) => !orderedIdSet.has(item.id)).map((item) => item.id),
        ]
        const { collectionRef, doc, setDoc } = await getCollectionDeps()
        await Promise.all(
          fullOrderedIds.map((id, index) => {
            const item = current.find((entry) => entry.id === id)
            if (!item) return Promise.resolve()
            return setDoc(doc(collectionRef, id), { ...item, order: (index + 1) * 10 }, { merge: true })
          }),
        )
      } catch {
        await fallbackRepository.reorder(orderedIds)
      }
    },

    subscribe(listener) {
      const unsubscribeFallback = fallbackRepository.subscribe(listener)
      let unsubscribeFirestore: (() => void) | undefined

      getCollectionDeps()
        .then(({ collectionRef, onSnapshot, orderBy, query }) => {
          unsubscribeFirestore = onSnapshot(
            query(collectionRef, orderBy('order', 'asc')),
            async (snapshot) => {
              const items = snapshot.docs.map((item) => item.data() as T)
              listener(mergeWithFallback(items, await fallbackRepository.list()))
            },
            () => undefined,
          )
        })
        .catch(() => undefined)

      return () => {
        unsubscribeFallback()
        unsubscribeFirestore?.()
      }
    },
  }
}
